import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import ClientsList from './pages/ClientsList';
import ClientForm from './pages/ClientForm';
import ClientDetail from './pages/ClientDetail';
import EmailTesting from './pages/EmailTesting';
import Filings from './pages/Filings';
import EmailSettings from './pages/EmailSettings';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import UserManagement from './pages/Usermanagement';
import Reports from './pages/Reports';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import SsoCallback from './pages/SsoCallback';
import Layout from './components/Layout';
import { useEffect } from 'react';
import { initSessionManager, destroySessionManager } from './utils/sessionManager';
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#000000',
      light: '#333333',
      dark: '#000000',
    },
    secondary: {
      main: '#667eea',
      light: '#8b9cec',
      dark: '#4a5ab3',
    },
    background: {
      default: '#FFFFFF',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#000000',
      secondary: '#6B7280',
    },
  },
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h4: {
      fontWeight: 700,
    },
    h6: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
  },
});

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem('isAuthenticated') === 'true';
  });

  // Initialize session manager when user is authenticated
  useEffect(() => {
    if (isAuthenticated) {
      initSessionManager();
console.log('🔐 Session monitoring active - 30 min inactivity timeout');
      
      // Cleanup only on unmount or when becoming unauthenticated
      return () => {
        destroySessionManager();
      };
    }
    // Don't destroy session manager when not authenticated - it's already not running
  }, [isAuthenticated]);

  const handleLogin = () => {
    setIsAuthenticated(true);
    localStorage.setItem('isAuthenticated', 'true');
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Routes>
          {/* Public Routes - No Authentication Required */}
          <Route
            path="/login"
            element={!isAuthenticated ? <Login onLogin={handleLogin} /> : <Navigate to="/dashboard" />}
          />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/sso-callback" element={<SsoCallback onLogin={handleLogin} />} />

          {/* Protected Routes - Authentication Required */}
          {isAuthenticated ? (
            <Route element={<Layout onLogout={handleLogout} />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/super-admin" element={<SuperAdminDashboard />} />
              <Route path="/clients" element={<ClientsList />} />
              <Route path="/clients/new" element={<ClientForm />} />
              <Route path="/clients/edit/:id" element={<ClientForm />} />
              <Route path="/clients/:id" element={<ClientDetail />} />
              <Route path="/filings" element={<Filings />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/settings/email" element={<EmailSettings />} />
              <Route path="/" element={<Navigate to="/dashboard" />} />
              <Route path="/email-testing" element={<EmailTesting />} />
              <Route path="users" element={<UserManagement />} />
            </Route>
          ) : (
            <Route path="*" element={<Navigate to="/login" />} />
          )}
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;